import {
  Box,
  Button,
  Input,
  Text,
  useToast,
  Stack,
  FormControl,
  FormLabel,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { multipleEpos } from "../service";
import ReactToPrint from "react-to-print";
import ReactDOM from "react-dom";
import html2pdf from "html2pdf.js";

const UploadEpos = ({ isLoading, setIsLoading }) => {
  const [jsonData, setJsonData] = useState(null);
  const [file, setFile] = useState(null);
  const [successItems, setSuccessItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const printRef = useRef();

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && uploadedFile.type === "application/json") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setJsonData(data);
          setFile(uploadedFile);
          toast({
            title: "File uploaded successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          toast({
            title: "Error parsing JSON file.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };
      reader.readAsText(uploadedFile);
    } else {
      toast({
        title: "Please upload a valid JSON file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSendFile = async (event) => {
    event.preventDefault();

    if (!file || !jsonData) {
      toast({
        title: "No file selected.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    const formattedData = jsonData.map((item) => ({
      licenseeIdNo: item.Licensee_Id_No,
      datetimeStamp: item.Datetime_Stamp,
      gtin: item.GTIN,
      packSize: parseInt(item.Pack_Size),
      mrp: parseFloat(item.MRP),
      quantity: parseInt(item.Quantity),
    }));

    try {
      const response = await multipleEpos(formattedData);
      const {
        successfulData = [],
        unsuccessfulData = [],
        message,
      } = response?.response?.data || {};

      const success = successfulData.map((item) => ({
        licenseeIdNo: item.licenseeIdNo,
        gtin: item.gtin,
        packSize: item.packSize,
        mrp: item.mrp,
        quantity: item.quantity,
      }));

      const failed = unsuccessfulData.map((item) => ({
        item: {
          licenseeIdNo: item.licenseeIdNo,
          gtin: item.gtin,
          packSize: item.packSize,
          mrp: item.mrp,
          quantity: item.quantity,
        },
        errorMessage: message || "Unknown error",
      }));

      setSuccessItems(success);
      setFailedItems(failed);

      toast({
        title:
          failed.length === 0 ? "All items processed." : "Some items failed.",
        status: failed.length === 0 ? "success" : "error",
        duration: 3000,
        isClosable: true,
      });

      setJsonData(null);
      setFile(null);
      onOpen();
    } catch (error) {
      setSuccessItems([]);
      setFailedItems(
        formattedData.map((item) => ({
          item,
          errorMessage: error.message,
        }))
      );
      toast({
        title: "Error processing file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      onOpen();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendFailedItems = async () => {
    setIsLoading(true);
    const failedData = failedItems.map((f) => f.item);

    try {
      const response = await multipleEpos(failedData);
      const {
        successfulData = [],
        unsuccessfulData = [],
        message,
      } = response?.response?.data || {};

      const newSuccess = successfulData.map((item) => ({
        licenseeIdNo: item.licenseeIdNo,
        gtin: item.gtin,
        packSize: item.packSize,
        mrp: item.mrp,
        quantity: item.quantity,
      }));

      const stillFailed = unsuccessfulData.map((item) => ({
        item,
        errorMessage: message || "Still failed",
      }));

      setSuccessItems((prev) => [...prev, ...newSuccess]);
      setFailedItems(stillFailed);

      toast({
        title:
          stillFailed.length === 0
            ? "Resent successfully."
            : "Some items still failed.",
        status: stillFailed.length === 0 ? "success" : "error",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setFailedItems(
        failedData.map((item) => ({ item, errorMessage: error.message }))
      );
      toast({
        title: "Error resending items.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
    <html>
    <head>
      <title>Print</title>
      <style>
        @media print {
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            width: 810px;
          }
        }

        body {
          font-family: Arial, sans-serif;
          width: 540px;
          padding: 30px;
        }

        .qty-value {
          font-size: 18px;
          font-weight: bold;
        }

        .item {
          display: flex;
          flex-direction: row;
          align-items: center;
          margin-bottom: 10px;
          font-size: 20px;
        }

        .qr {
          text-align: center;
          margin-right: 40px;
          margin-top:10px;
        }

        .gtin-label {
          margin-top: 40px;
          font-size: 10px;
          word-break: break-word;
          max-width: 100px;
        }

        .details {
          font-size: 10px;
          text-align: center;
          line-height: 1.3;
          margin: 10px 40px 0px 0px;
        }

        .barcode {
          text-align: center;
          margin-right: 50px;
          max-width: 160px;
        }

        hr {
          margin: 10px 0;
          border: 0;
          border-top: 1px dashed #000;
        }

        .qr svg {
          width: 100px !important;
          height: 100px !important;
        }
      </style>
    </head>
    <body>
  `);

    jsonData.forEach((item, index) => {
      const qrId = `qr-${index}`;
      const barcodeId = `barcode-${index}`;
      printWindow.document.write(`
      <div class="item">
        <div class="qr" id="${qrId}"></div>
        <div class="details">
          <p><strong>Licensee ID:</strong> ${item.Licensee_Id_No}</p>
          <p><strong>Item Name:</strong> ${item.Itemname}</p>
          <p><strong>${item.QRCODE ? "QRCODE" : "GTIN"}:</strong> ${item.QRCODE || item.GTIN}</p>
          <p><strong>Pack:</strong> ${item.Pack_Size}</p>
          <p><strong>MRP:</strong> ₹${item.MRP}</p>
          <p><strong>Qty:</strong> <span class="qty-value">${item.Quantity}</span></p>
        </div>
        <div class="barcode" id="${barcodeId}"></div>
      </div>
      <hr />
    `);
    });

    printWindow.document.write(`</body></html>`);
    printWindow.document.close();

    printWindow.onload = () => {
      jsonData.forEach((item, index) => {
        const qrContainer = printWindow.document.getElementById(`qr-${index}`);
        const barcodeContainer = printWindow.document.getElementById(`barcode-${index}`);

        const qrValue = String((item.QRCODE || item.GTIN || "")).trim();
        const barcodeValue = String((item.GTIN || item.QRCODE || "")).trim();
        const isQRCode = !!item.QRCODE;
        const qrSize = isQRCode ? 80 : 100;
        const isQRCodeOnly = !!item.QRCODE && !item.GTIN;

        // === QR Code ===
        const qrTempDiv = document.createElement("div");
        ReactDOM.render(
          <QRCodeSVG value={qrValue} size={qrSize} />,
          qrTempDiv
        );

        const qrLabel = document.createElement("div");
        qrLabel.className = "gtin-label";
        qrLabel.textContent = qrValue;

        const qrWrapper = document.createElement("div");
        qrWrapper.appendChild(qrTempDiv.firstChild);
        qrWrapper.appendChild(qrLabel);
        qrContainer.innerHTML = qrWrapper.outerHTML;

        // === Barcode ===
        const barcodeTempDiv = document.createElement("div");
        ReactDOM.render(
          <Barcode
            value={barcodeValue}
            format="CODE128"
            width={isQRCodeOnly ? 0.5 : 1.5}
            height={isQRCodeOnly ? 40 : 50}
            fontSize={isQRCodeOnly ? 8 : 12}
            displayValue={false}
            background="#fff"
            lineColor="#000"
          />,
          barcodeTempDiv
        );

        const barcodeLabel = document.createElement("div");
        barcodeLabel.className = "gtin-label";
        barcodeLabel.textContent = barcodeValue;

        barcodeTempDiv.appendChild(barcodeLabel);
        barcodeContainer.innerHTML = barcodeTempDiv.innerHTML;
      });

      printWindow.focus();
      printWindow.print();
    };
  };

  const handleDownloadPDF = async () => {
    const container = document.createElement("div");
    container.className = "pdf-container";

    // === Inject PDF Styles (NO CHANGE)
    const style = document.createElement("style");
    style.innerHTML = `
    .pdf-container {
      font-family: Arial, sans-serif;
      width: 540px;
      padding: 30px;
    }
    .pdf-container .qty-value {
      font-size: 18px;
      font-weight: bold;
    }
    .pdf-container .item {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 10px;
      font-size: 20px;
    }
    .pdf-container .qr {
      text-align: center;
      margin-right: 40px;
      margin-top: 10px;
    }
    .pdf-container .gtin-label {
      margin-top: 40px;
      font-size: 10px;
      word-break: break-word;
      max-width: 100px;
    }
    .pdf-container .details {
      font-size: 10px;
      text-align: center;
      line-height: 1.3;
      margin: 10px 40px 0px 0px;
    }
    .pdf-container .barcode {
      text-align: center;
      margin-right: 50px;
      max-width: 160px;
    }
    .pdf-container hr {
      margin: 10px 0;
      border: 0;
      border-top: 1px dashed #000;
    }
  `;
    container.appendChild(style);

    // === Build Content (NO CHANGES to QR + barcode layout)
    jsonData.forEach((item, index) => {
      const qrValue = item.QRCODE || item.GTIN;
      const barcodeValue = item.GTIN || item.QRCODE;
      const isQRCodeOnly = !!item.QRCODE && !item.GTIN;

      const wrapper = document.createElement("div");
      wrapper.className = "item";

      const qrDiv = document.createElement("div");
      qrDiv.className = "qr";
      const qrTempDiv = document.createElement("div");
      ReactDOM.render(<QRCodeSVG value={qrValue} size={isQRCodeOnly ? 80 : 100} />, qrTempDiv);
      const qrLabel = document.createElement("div");
      qrLabel.className = "gtin-label";
      qrLabel.textContent = qrValue;
      qrDiv.appendChild(qrTempDiv.firstChild);
      qrDiv.appendChild(qrLabel);

      const details = document.createElement("div");
      details.className = "details";
      details.innerHTML = `
      <p><strong>Item Name:</strong> ${item.Itemname}</p>
      <p><strong>${item.QRCODE ? "QRCODE" : "GTIN"}:</strong> ${qrValue}</p>
      <p><strong>Pack:</strong> ${item.Pack_Size}</p>
      <p><strong>MRP:</strong> ₹${item.MRP}</p>
      <p><strong>Qty:</strong> <span class="qty-value">${item.Quantity}</span></p>
    `;

      const barcodeDiv = document.createElement("div");
      barcodeDiv.className = "barcode";
      const barcodeTempDiv = document.createElement("div");
      ReactDOM.render(
        <Barcode
          value={barcodeValue}
          format="CODE128"
          width={isQRCodeOnly ? 0.5 : 1.5}
          height={isQRCodeOnly ? 40 : 50}
          fontSize={isQRCodeOnly ? 8 : 12}
          displayValue={false}
          background="#fff"
          lineColor="#000"
        />,
        barcodeTempDiv
      );
      const barcodeLabel = document.createElement("div");
      barcodeLabel.className = "gtin-label";
      barcodeLabel.textContent = barcodeValue;
      barcodeTempDiv.appendChild(barcodeLabel);
      barcodeDiv.appendChild(barcodeTempDiv);

      wrapper.appendChild(qrDiv);
      wrapper.appendChild(details);
      wrapper.appendChild(barcodeDiv);
      container.appendChild(wrapper);
      const divider = document.createElement("hr");
      container.appendChild(divider);

      if ((index + 1) % 5 === 0 && index !== jsonData.length - 1) {
        const pageBreak = document.createElement("div");
        pageBreak.style.pageBreakAfter = "always";
        container.appendChild(pageBreak);
      }

    });

    try {
      // === Step 1: Generate Blob
      const pdfBlob = await html2pdf()
        .set({
          margin: 0.5,
          filename: "user-data.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .outputPdf("blob");

      // === Step 2: Convert Blob to File
      const pdfFile = new File([pdfBlob], "user-data.pdf", { type: "application/pdf" });

      // === Step 3: Download Locally
      const blobUrl = URL.createObjectURL(pdfFile);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "user-data.pdf";
      link.click();
      URL.revokeObjectURL(blobUrl);

      toast({
        title: "PDF downloaded successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // === Step 4: Share Prompt
      const confirmShare = window.confirm("Do you want to share this PDF on WhatsApp?");
      if (!confirmShare) return;

      const number = window.prompt("Please enter WhatsApp number (with country code):");
      if (!number) {
        toast({
          title: "No number provided",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // === Step 5: Upload + Share
      const formData = new FormData();
      formData.append("pdf", pdfFile);
      formData.append("number", number);
      formData.append("message", "Here is your PDF from TDR Software Pvt Ltd");

      const response = await fetch("https://api.epos.tdrsoftware.in/share-pdf-on-whatsapp", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      if (response.ok) {
        toast({
          title: "PDF shared on WhatsApp!",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        throw new Error(result.msg || "Failed to share on WhatsApp");
      }
    } catch (error) {
      console.error("PDF Share Error:", error);
      toast({
        title: "Error sharing PDF",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };





  return (
    <Box as="form" onSubmit={handleSendFile}>
      <Stack spacing={4}>
        <FormControl>
          <FormLabel>Upload JSON File</FormLabel>
          <Flex alignItems="center">
            <Input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              id="file-upload"
              display="none"
            />
            <Button
              as="label"
              htmlFor="file-upload"
              cursor="pointer"
              width="100%"
            >
              Choose File
            </Button>
          </Flex>
        </FormControl>
        {jsonData && jsonData.length > 0 && (
          <Box bg="white" boxShadow="md" p={2} maxH="400px" overflowY="auto">
            {jsonData.map((item, index) => {
              const qrValue = item.QRCODE || item.GTIN;
              const isQrcodeUrl = !!item.QRCODE;

              return (
                <Flex
                  key={index}
                  p={4}
                  borderBottom="1px solid #ddd"
                  justify="space-between"
                  align="center"
                  gap={8}
                >
                  {/* QR Code */}
                  <Box textAlign="center">
                    <QRCodeSVG value={qrValue} size={90} />
                    <Text fontSize="xs" mt={8} color="gray.500">
                      {isQrcodeUrl ? "Scans to QRCODE URL" : "Scans to GTIN"}
                    </Text>
                    <Text fontSize="xs" mt={1} wordBreak="break-word" maxW="140px">
                      {qrValue}
                    </Text>
                  </Box>

                  {/* Product Details */}
                  <Box flex="1" textAlign="left" px={4}>
                    <Text>Item Name: {item.Itemname}</Text>
                    <Text>Licensee Id No: {item.Licensee_Id_No}</Text>
                    <Text>GTIN: {item.GTIN}</Text>
                    <Text>Pack Size: {item.Pack_Size}</Text>
                    <Text>MRP: ₹{item.MRP}</Text>
                    <Text>Quantity: {item.Quantity}</Text>
                  </Box>

                  {/* Barcode with fixed width container */}
                  <Box textAlign="center" maxW="180px" overflow="hidden">
                    <Barcode
                      value={qrValue}
                      format="CODE128"
                      width={1.5}
                      height={50}
                      fontSize={12}
                      displayValue={false}
                    />
                    <Text fontSize="xs" color="gray.500" isTruncated maxW="160px">
                      {qrValue}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Box>
        )}



        <Button type="submit" colorScheme="blue" isLoading={isLoading}>
          Send
        </Button>

        <Button colorScheme="green" onClick={handlePrint}>
          Print
        </Button>
        <Button colorScheme="green" onClick={handleDownloadPDF}>
          Download Pdf
        </Button>

      </Stack>

      <div style={{ display: "none" }}>
        <div ref={printRef}>
          {jsonData && jsonData.length > 0 && (
            <Box bg="white" boxShadow="md" p={4}>
              {jsonData
                .reduce((acc, _, i) =>
                  i % 6 === 0 ? [...acc, jsonData.slice(i, i + 6)] : acc, [])
                .map((group, pageIndex) => (
                  <Box key={pageIndex} mb={20} page-break-after="always">
                    {group.map((item, index) => (
                      <Flex
                        key={index}
                        p={4}
                        borderBottom="1px solid #ddd"
                        justify="space-between"
                        align="center"
                        gap={8}
                      >
                        <Box textAlign="center">
                          <QRCodeSVG value={item.GTIN} size={80} />
                          <Text fontSize="sm" mt={1}>
                            {item.GTIN}
                          </Text>
                        </Box>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text>Licensee Id No: {item.Licensee_Id_No}</Text>
                          <Text>GTIN: {item.GTIN}</Text>
                          <Text>Pack Size: {item.Pack_Size}</Text>
                          <Text>MRP: {item.MRP}</Text>
                          <Text>Quantity: {item.Quantity}</Text>
                        </Box>
                        <Box>
                          <Barcode
                            value={item.GTIN}
                            width={1.5}
                            height={50}
                            fontSize={12}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </div>
      </div>


      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>File Processing Results</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontWeight="bold">Successful Items:</Text>
            <Box
              maxH="300px"
              overflowY="auto"
              borderWidth="1px"
              p={2}
              borderRadius="md"
            >
              {successItems.length > 0 ? (
                successItems.map((item, index) => (
                  <Flex
                    key={index}
                    p={2}
                    borderBottom="1px solid #ddd"
                    justify="space-between"
                    align="center"
                    gap={4}
                  >
                    <Box textAlign="center">
                      <QRCodeSVG value={item.gtin} size={80} />
                      <Text fontSize="sm" mt={1}>
                        {item.gtin}
                      </Text>
                    </Box>
                    <Box flex="1" textAlign="left" px={4}>
                      <Text>Licensee Id No: {item.licenseeIdNo}</Text>
                      <Text>GTIN: {item.gtin}</Text>
                      <Text>Pack Size: {item.packSize}</Text>
                      <Text>MRP: {item.mrp}</Text>
                      <Text style={{ fontSize: 18, fontWeight: "bold" }}>
                        Quantity: {item.quantity}
                      </Text>
                    </Box>
                    <Box>
                      <Barcode
                        value={item.gtin}
                        width={1.5}
                        height={50}
                        fontSize={12}
                      />
                    </Box>
                  </Flex>
                ))
              ) : (
                <Text>No successful items.</Text>
              )}
            </Box>

            <Text fontWeight="bold" mt={4}>
              Failed Items:
            </Text>
            <Box
              maxH="300px"
              overflowY="auto"
              borderWidth="1px"
              p={2}
              borderRadius="md"
            >
              {failedItems.length > 0 ? (
                failedItems.map((f, index) => (
                  <Box key={index} p={2} borderBottom="1px solid #ddd">
                    <Text>Licensee Id No: {f.item.licenseeIdNo}</Text>
                    <Text>GTIN: {f.item.gtin}</Text>
                    <Text>Pack Size: {f.item.packSize}</Text>
                    <Text>MRP: {f.item.mrp}</Text>
                    <Text>Quantity: {f.item.quantity}</Text>
                    <Text color="red">Error: {f.errorMessage}</Text>
                  </Box>
                ))
              ) : (
                <Text>No failed items.</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleResendFailedItems}
              colorScheme="blue"
              isLoading={isLoading}
            >
              Resend Failed Items
            </Button>
            <Button onClick={onClose} ml={3}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UploadEpos;
